/*
 * Pre-order line item attribute (_preorder)
 *
 * A line for a product whose custom.pre_order_ship_date metafield holds a future
 * date carries a _preorder attribute set to that date (YYYY-MM-DD). The attribute
 * is stamped at add-to-cart time by snippets/preorder-line-item-property.liquid,
 * and this file keeps it honest afterwards: on every cart mutation, when the cart
 * drawer or cart page is opened, and immediately before checkout, it re-reads the
 * live metafield state and adds, updates or removes _preorder as needed.
 *
 * The "is this still a pre-order?" rule lives in Liquid
 * (snippets/preorder-ship-date.liquid, surfaced by sections/api-cart-preorder.liquid)
 * so add-to-cart and revalidation can never disagree about it.
 *
 * Other line item attributes are never touched: _backordered, _promised_by, _d,
 * _sale and friends are read back off the line and resent verbatim.
 */

(function () {
  const ATTRIBUTE = "_preorder";
  const SECTION_ID = "api-cart-preorder";

  /*
   * When a product's ship date passes (or the metafield is cleared) while the item
   * is already in the cart, drop _preorder from the line. This mirrors cab-backend's
   * applyPreorderAttribute, which strips the attribute from stylist-built orders
   * under the same conditions, so a shopper cart and a stylist order agree about the
   * same product. Pending final confirmation from the business — if they decide a
   * stamped line should keep its original date, flip this to false and nothing else
   * needs to change.
   */
  const REMOVE_WHEN_NO_LONGER_FUTURE = true;

  // Each pass rechecks its own work, so a second pass normally finds nothing to do.
  // The cap only exists so a line we can never satisfy can't spin forever.
  const MAX_PASSES = 3;
  const DEBOUNCE_MS = 250;
  // Never hold up checkout on a slow or wedged cart request.
  const CHECKOUT_TIMEOUT_MS = 5000;

  const selectors = {
    cartDrawer: '[data-drawer="drawer-cart"]',
    cartPage: '[data-section-type="cart"]',
    payload: "[data-preorder-cart]",
  };

  let inFlight = null;
  let rerunQueued = false;
  let debounceTimer = null;
  // Once checkout is under way the page is on its way out, and a cart re-render
  // request would turn into window.location.reload() on the cart page and race the
  // redirect. Never reset: the page is leaving either way.
  let leavingForCheckout = false;

  function rootUrl() {
    return (window.theme && window.theme.routes && window.theme.routes.root_url) || "/";
  }

  function cartUrl() {
    return (window.theme && window.theme.routes && window.theme.routes.cart) || "/cart";
  }

  // The current cart, plus the ship date each line should be carrying, as rendered
  // by sections/api-cart-preorder.liquid.
  async function fetchCartState() {
    const response = await fetch(`${rootUrl()}?section_id=${SECTION_ID}`, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });

    if (!response.ok) {
      throw new Error(`Could not load pre-order cart state (${response.status})`);
    }

    const holder = document.createElement("div");
    holder.innerHTML = await response.text();

    const payload = holder.querySelector(selectors.payload);
    if (!payload) {
      throw new Error("Pre-order cart state is missing from the rendered section");
    }

    return JSON.parse(payload.textContent);
  }

  function currentValue(line) {
    const value = (line.properties || {})[ATTRIBUTE];
    return typeof value === "string" ? value : "";
  }

  function hasAttribute(line) {
    return Object.prototype.hasOwnProperty.call(line.properties || {}, ATTRIBUTE);
  }

  // "" means the product is not a pre-order right now, so the line should not carry
  // the attribute at all.
  function wantedValue(line) {
    return typeof line.ship_date === "string" ? line.ship_date : "";
  }

  function needsUpdate(line) {
    const wanted = wantedValue(line);
    if (wanted) return currentValue(line) !== wanted;
    if (!REMOVE_WHEN_NO_LONGER_FUTURE) return false;
    // Presence, not value: a line left carrying an empty _preorder still has the key
    // for downstream code to trip over, so it counts as needing the removal.
    return hasAttribute(line);
  }

  /*
   * /cart/change.js overwrites a line's properties wholesale, so every attribute the
   * line already has has to be sent back with it, and the way to remove _preorder is
   * to leave the key out rather than blank it — a blank value stays on the line and
   * reads downstream as "this line has a _preorder".
   */
  function nextProperties(line) {
    const existing = line.properties || {};
    const properties = {};

    Object.keys(existing).forEach((key) => {
      if (key === ATTRIBUTE) return;
      const value = existing[key];
      if (value === null || value === undefined) return;
      properties[key] = value;
    });

    const wanted = wantedValue(line);
    if (wanted) properties[ATTRIBUTE] = wanted;

    return properties;
  }

  async function postCart(endpoint, body) {
    const response = await fetch(`${cartUrl()}/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`cart/${endpoint} failed (${response.status})`);
    }

    return response.json();
  }

  /*
   * Shopify ignores an empty properties object — "It's not possible to set a line
   * item's properties property to an empty object once a value is set" — so when
   * _preorder is a line's only attribute, change.js cannot take it off and the line
   * has to be rebuilt instead.
   *
   * Add first, remove second. The add can fail (the shopper may be holding the last
   * of the stock), and if it does we leave the line exactly as it was: a stale
   * attribute is worth far more than a lost item.
   */
  async function rebuildWithoutProperties(line) {
    const item = { id: line.variant_id, quantity: line.quantity };
    if (line.selling_plan_id) item.selling_plan = line.selling_plan_id;

    await postCart("add.js", { items: [item] });
    return postCart("change.js", { id: line.key, quantity: 0 });
  }

  async function applyChange(line) {
    const properties = nextProperties(line);

    if (!Object.keys(properties).length) {
      return rebuildWithoutProperties(line);
    }

    return postCart("change.js", { id: line.key, properties });
  }

  async function runPasses() {
    let changed = false;

    for (let pass = 0; pass < MAX_PASSES; pass++) {
      let lines;
      try {
        lines = await fetchCartState();
      } catch (error) {
        // Report what we already changed regardless, so the cart still re-renders
        // with the keys those changes produced.
        console.error("[preorder] cart revalidation failed", error);
        return changed;
      }

      const stale = lines.filter(needsUpdate);

      if (!stale.length) return changed;

      // Sequentially: each change re-keys its line, and cart writes are not safe to
      // run in parallel.
      for (const line of stale) {
        try {
          await applyChange(line);
          changed = true;
        } catch (error) {
          console.warn("[preorder] line update failed", error);
        }
      }
    }

    return changed;
  }

  /*
   * Changing properties gives a line a new key, which invalidates the keys the
   * rendered cart is holding for its remove links and quantity inputs, so ask the
   * theme to re-render once we've actually changed something.
   */
  function refreshCartUi() {
    document.dispatchEvent(new CustomEvent("theme:cart:reload", { bubbles: true }));
  }

  function sync() {
    if (inFlight) {
      rerunQueued = true;
      return inFlight;
    }

    inFlight = runPasses()
      .then((changed) => {
        if (changed && !leavingForCheckout) refreshCartUi();
        return changed;
      })
      .catch((error) => {
        console.error("[preorder] cart revalidation failed", error);
        return false;
      })
      .finally(() => {
        inFlight = null;
        if (rerunQueued) {
          rerunQueued = false;
          sync();
        }
      });

    return inFlight;
  }

  // Cart mutations arrive in bursts (add-to-cart fires a change and a reload, the
  // benefits selector rewrites several lines in a row), so collapse them.
  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => sync(), DEBOUNCE_MS);
  }

  /*
   * Checkout is the last chance to get the attribute right, so it waits for the
   * revalidation — but only for so long. A cart request that stalls must not strand
   * the shopper on the cart page.
   */
  function wrapCustomCheckout() {
    const original = window.customCheckout;
    if (typeof original !== "function" || original.preorderWrapped) return;

    const wrapped = function () {
      const context = this;
      const args = arguments;

      leavingForCheckout = true;
      clearTimeout(debounceTimer);

      const timeout = new Promise((resolve) => setTimeout(resolve, CHECKOUT_TIMEOUT_MS));

      Promise.race([sync(), timeout])
        .catch((error) => console.error("[preorder] revalidation before checkout failed", error))
        .then(() => original.apply(context, args));
    };

    wrapped.preorderWrapped = true;
    window.customCheckout = wrapped;
  }

  function listen() {
    // global-variables.liquid defines customCheckout inline in the head, so it is
    // already there when this deferred script runs — but wrap again once the page
    // has parsed in case anything defines it later.
    wrapCustomCheckout();

    // Covers add-to-cart, quantity changes, removals and the stylist benefits
    // selector — every path in the theme dispatches this after mutating the cart.
    document.addEventListener("theme:cart:change", scheduleSync);
    document.addEventListener("theme:cart:init", scheduleSync);

    // Catches a metafield that changed while the cart sat untouched, at the two
    // moments the shopper is actually looking at the cart.
    const cartDrawer = document.querySelector(selectors.cartDrawer);
    if (cartDrawer) {
      cartDrawer.addEventListener("theme:drawer:open", scheduleSync);
    }

    if (document.querySelector(selectors.cartPage)) {
      scheduleSync();
    }
  }

  wrapCustomCheckout();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", listen);
  } else {
    listen();
  }
})();
